import type { Product, ProductTag } from "../types";

/**
 * Tag key and values used to flag products with built-in AI features.
 *
 * These are the slug IDs seeded in `seedTagDefinitions` (backend
 * cmd/server/migrate.go). They are never shown to users -- the UI always
 * renders the seeded `Label` instead.
 */
export const AI_FEATURES_TAG_KEY = "ai-features";
export const AI_FEATURES_YES = "yes";
export const AI_FEATURES_NO = "no";

/**
 * Read the AI features tag off a product.
 *
 * Returns "yes", "no", or undefined when the product has never been assessed.
 * The three states are deliberately distinct: "no" records that somebody looked
 * and found no AI, which is not the same as nobody having looked yet.
 */
export function getAiFeaturesValue(
  product: Pick<Product, "productTags">,
): string | undefined {
  const tags: ProductTag[] = product.productTags ?? [];
  return tags.find((t) => t.key === AI_FEATURES_TAG_KEY)?.value;
}

/** True when the product is flagged as having built-in AI features. */
export function hasAiFeatures(product: Pick<Product, "productTags">): boolean {
  return getAiFeaturesValue(product) === AI_FEATURES_YES;
}

/**
 * Match a product against the AI features filter.
 *
 * `filterValue` is "yes", "no", or "" / undefined for "no filter". "no"
 * deliberately matches only products explicitly flagged as having no AI, not
 * products that were never assessed -- otherwise the unassessed backlog would
 * be hidden inside a count that reads as "confirmed no AI".
 */
export function matchesAiFeaturesFilter(
  product: Pick<Product, "productTags">,
  filterValue: string | undefined,
): boolean {
  if (!filterValue) return true;
  return getAiFeaturesValue(product) === filterValue;
}
